import {forwardRef} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {AlertTriangleIcon, CheckCircleIcon} from '@components/ui/icon'
import {useGetCommonIssues} from '@features/insights/queries/useGetCommonIssues'
import {Opportunities} from './opportunities'
import {Skeleton} from '@components/ui/skeleton'
import {MissingCoverage} from './missing-coverage'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams'
import {InsightsFilterParam} from './insights-filters-select'
import {ErrorBox} from '@components/commons/error-box'
import {Button} from '@components/ui/button'

export const CommonIssues = forwardRef<HTMLDivElement, {isInsightsVisible: boolean}>(({isInsightsVisible}, ref) => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(InsightsFilterParam)
    const {data, isLoading, isError, refetch} = useGetCommonIssues(searchParams.period, isInsightsVisible)

    const opportunities = data?.topOpportunities ?? []
    const missingCoverageData = data?.missingCoverageSpaces ?? []

    return (
        <div ref={ref} className="flex flex-col gap-6">
            {isError ? (
                <ErrorBox
                    title={t('errors:default')}
                    icon={<AlertTriangleIcon />}
                    cta={<Button onClick={() => refetch()}>{t('commons:retry')}</Button>}
                />
            ) : isLoading ? (
                <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-3 md:gap-6">
                    {Array.from({length: 6}).map((_, index) => (
                        <Skeleton key={index} />
                    ))}
                </div>
            ) : !!opportunities.length || missingCoverageData.length ? (
                <>
                    {!!opportunities.length && <Opportunities opportunities={opportunities} />}
                    {!!missingCoverageData.length && <MissingCoverage missingCoverageData={missingCoverageData} />}
                </>
            ) : (
                <div className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-success-200 bg-success-50 px-4 py-12 [&_svg]:mb-4 [&_svg]:size-12 [&_svg]:fill-success-600 [&_h4]:text-display-xs [&_h4]:font-bold [&_p]:text-center [&_p]:text-xl">
                    <CheckCircleIcon />
                    <h4>{t('insights:issues:empty_section_title')}</h4>
                    <p>
                        <Trans i18nKey="insights:issues:empty_section_description" components={{br: <br />}} />
                    </p>
                </div>
            )}
        </div>
    )
})
